import React from "react";
import { TestTubeIcon as TestTube } from "@phosphor-icons/react";

/**
 * DNA-Verified badge — appears on a dog after admin approval of its
 * $75 DNA Pedigree Verification submission.
 *
 * Visual treatment is intentionally seal-like (vs. the flatter status
 * tags elsewhere in the app):
 *   - Diagonal gold-foil gradient with a bright crest near the top-left
 *     to suggest light catching the metal
 *   - Inset highlight ring + bottom shadow line give the pill a stamped,
 *     embossed feel
 *   - Outer drop shadow lifts it off marketplace cards & pedigree nodes
 *
 * Two sizes — sm for cards/pedigree nodes, md for inline detail rows,
 * lg for the dog detail page header.
 */
export default function DNAVerifiedBadge({
  verified = false,
  size = "sm",
  showLabel = true,
  className = "",
  linkToExplainer = false,
}) {
  if (!verified) return null;
  const px = size === "lg" ? 18 : size === "md" ? 14 : 12;
  const padding =
    size === "lg" ? "px-3 py-1.5" : size === "md" ? "px-2.5 py-1" : "px-2 py-0.5";
  const fontSize = size === "lg" ? "text-[11px]" : "text-[10px]";

  const inner = (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-bold uppercase tracking-[0.15em] ${padding} ${fontSize} ${className}`}
      data-testid="badge-dna-verified"
      title="DNA-Verified — parentage confirmed via Embark / Wisdom Panel / VGL panel (admin review; not independent lab test)"
      style={{
        background:
          "linear-gradient(135deg, #F4DC85 0%, #E8C75E 35%, #C9A227 70%, #A37F11 100%)",
        color: "#3D2E00",
        border: "1px solid rgba(122,92,0,0.35)",
        boxShadow:
          "inset 0 1px 0 rgba(255,247,210,0.85), inset 0 -1px 0 rgba(122,92,0,0.35), 0 1px 3px rgba(122,92,0,0.25)",
        textShadow: "0 1px 0 rgba(255,247,210,0.55)",
      }}
    >
      <TestTube
        size={px}
        weight="fill"
        style={{ filter: "drop-shadow(0 1px 0 rgba(255,247,210,0.6))" }}
      />
      {showLabel && "DNA Verified"}
    </span>
  );
  if (linkToExplainer) {
    return (
      <a href="/dna-verified" target="_blank" rel="noopener noreferrer" className="no-underline">
        {inner}
      </a>
    );
  }
  return inner;
}
